import { EMPTY, merge, of } from 'rxjs';
import { catchError, finalize, mergeMap, tap, take, filter } from 'rxjs/operators';

import Global from '../global-selectors';
import * as PrintActions from '../../store/printing/print.actions';
import { startBulkPurchaseCostReview } from '../bulk/purchase/bulk.purchase.cost-review.observables';

import {
  determineShouldShowPrintDialog,
  determineShouldShowCostReviewByError,
  determineShouldShowErrorsOrWarnings,
} from '../quickship/quickship.helpers';
import { showErrorCreatingLabelNotification } from './csw.helpers';
import { handleQuickshipPrint } from '../../store/printing/print.quickship.observables';
import { createLabelSubject } from '../../fulfillment-planner/context';
import { getPlansWithDangerousGoods, trackLabelCreationComplete, trackLabelCreationError } from '../../store/labels/tracking-events';

import * as BulkApi from '../../store/bulk/bulk.api';

import * as Actions from './csw.actions';
import * as EntitiesAction from '../entities/entities.actions';
import * as SellerActions from '../../store/seller/seller.actions';
import { getInsurancePreferenceForAnalytics } from '../../hooks/use-insurance-preference';
import { showPostLabelReminderIfNeeded } from '../customs-documents/customs-documents.label-purchase';

const resetLabelCreationCompleteAfterBulkLoad = (action$, fulfillmentPlanId) => {
  return action$.pipe(
    filter((action) => action.type === EntitiesAction.ENTITIES_BULKLOAD && action?.entities?.fulfillments),
    take(1),
    tap(() => {
      createLabelSubject.next({
        fulfillmentPlanId,
        didLabelCreationComplete: false,
      });
    }),
    mergeMap(() => EMPTY),
  );
};

export const handleLabelPurchaseSuccess = ({
  fulfillmentPlanId,
  labelObject,
  autoPrint,
  doNotPrint,
  defaultPrinterAutoPrintSetting,
  quickshipProps,
  printSettings,
  getState,
  action$,
}) => {
  const state = getState();
  const { sellerLabelProviderId } = labelObject;
  const hasWalletProvider = Global.selectSellerLabelProviderIsWalleted(state, {
    sellerLabelProviderId,
  });
  const hasCreatedLabel = Global.selectSellerHasCreatedLabel(state);
  trackLabelCreationComplete(
    fulfillmentPlanId,
    labelObject,
    doNotPrint,
    hasWalletProvider,
    getPlansWithDangerousGoods([fulfillmentPlanId], state),
    getInsurancePreferenceForAnalytics(),
  );
  createLabelSubject.next({
    fulfillmentPlanId,
    isCreatingLabel: false,
    errorMessages: [],
    didLabelCreationComplete: true,
  });

  const postLabelSuccessAction = hasCreatedLabel ? EMPTY : of(SellerActions.getSeller());

  if (doNotPrint) {
    return merge(
      of(Actions.createLabelSuccess(fulfillmentPlanId, labelObject, autoPrint, doNotPrint, { sourceHasHandled: true })),
      resetLabelCreationCompleteAfterBulkLoad(action$, fulfillmentPlanId),
      showPostLabelReminderIfNeeded(fulfillmentPlanId, state),
      postLabelSuccessAction,
    );
  }

  return merge(
    // TODO: cleanup the big spiderweb off this action
    of(Actions.createLabelSuccess(fulfillmentPlanId, labelObject, autoPrint, doNotPrint, { sourceHasHandled: true })),
    showPostLabelReminderIfNeeded(fulfillmentPlanId, state),
    BulkApi.fulfillmentPlanToFulfillment([fulfillmentPlanId]).pipe(
      mergeMap((data = []) => {
        const fetchedFulfillmentIds = data.map((row = {}) => row.fulfillmentId);
        if (!fetchedFulfillmentIds.length) {
          return EMPTY;
        }

        if (quickshipProps.shouldUseQuickship) {
          return merge(
            handleQuickshipPrint(
              [fulfillmentPlanId],
              fetchedFulfillmentIds,
              printSettings,
              defaultPrinterAutoPrintSetting,
              determineShouldShowPrintDialog(quickshipProps),
            ),
            resetLabelCreationCompleteAfterBulkLoad(action$, fulfillmentPlanId),
          );
        }
        return merge(
          of(PrintActions.printLabel([fulfillmentPlanId], fetchedFulfillmentIds, false, false, defaultPrinterAutoPrintSetting)),
          resetLabelCreationCompleteAfterBulkLoad(action$, fulfillmentPlanId),
        );
      }),
      catchError(() => EMPTY),
      finalize(() => {
        createLabelSubject.next({
          fulfillmentPlanId,
          isCreatingLabel: false,
          errorMessages: [],
        });
      }),
    ),
    postLabelSuccessAction,
  );
};

export const handleLabelPurchaseFailure = ({
  fulfillmentPlanId,
  errorResponse,
  friendlyErrors,
  doNotPrint,
  quickshipProps,
  getState,
  action$,
  hasAlreadyViewedCostReview,
}) => {
  trackLabelCreationError(fulfillmentPlanId, friendlyErrors);

  const shouldShowCostReview = determineShouldShowCostReviewByError(errorResponse, quickshipProps) && !hasAlreadyViewedCostReview;
  if (determineShouldShowErrorsOrWarnings(quickshipProps) && !shouldShowCostReview) {
    showErrorCreatingLabelNotification();
  }

  if (shouldShowCostReview) {
    return merge(
      of(Actions.createLabelFailure(fulfillmentPlanId, friendlyErrors, !doNotPrint, false)),
      startBulkPurchaseCostReview({
        action$,
        included: [fulfillmentPlanId],
        excluded: undefined,
        errored: undefined,
        andPrint: !doNotPrint,
        getState,
        shouldShowLegacyCostReview: true,
      }),
    );
  }
  return of(Actions.createLabelFailure(fulfillmentPlanId, friendlyErrors, !doNotPrint, true));
};
